import { useMemo, useRef } from 'react';
import { ValidationLevel, ValidationProps } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Configuration } from 'app/components/metro-line/types';
import { ExternalUrlAdminTranslation } from '../../constants';
import { ExternalUrlItemValidation } from '../types-store';
import { ExternalUrlSectionType } from './types';
import { ExternalUrlActionType } from 'app/area/external-urls-administration/form/types';

const isValid = (validationProps: ValidationProps[], level: ValidationLevel) =>
  !validationProps.some(prop => (prop?.messages?.length ?? 0) > 0 && prop?.level === level);

export const getExternalUrlRoute = (
  actionType: ExternalUrlActionType,
  id: number,
  section?: ExternalUrlSectionType
) => actionType && {
  [ExternalUrlActionType.add]: routeConstants.externalUrlAddFor(section),
  [ExternalUrlActionType.edit]: routeConstants.externalUrlEditFor(id, section)
}[actionType];

export const useExternalUrlConfiguration = (
  actionType: ExternalUrlActionType,
  id: number,
  validationErrors: ExternalUrlItemValidation
): Configuration<ExternalUrlSectionType>[] => {

  const detailsSectionRef = useRef<HTMLDivElement>();
  const visibilitySectionRef = useRef<HTMLDivElement>();
  const translationsSectionRef = useRef<HTMLDivElement>();

  return useMemo(() => {

    const getSectionRoute = (section: ExternalUrlSectionType) => getExternalUrlRoute(actionType, id, section);
    const renderLanguageOnTop = !isDefinedIncludingZero(id);
    const languageSection = {
      section: ExternalUrlSectionType.Translations,
      visible: true,
      url: getSectionRoute(ExternalUrlSectionType.Translations),
      iconUrl: '/i/case-sections/actions.svg',
      label: (translation: ExternalUrlAdminTranslation) => translation.descriptions_and_translations,
      ref: translationsSectionRef,
      isValid: isValid([validationErrors.language], ValidationLevel.error)
    };

    return [
      ...(renderLanguageOnTop ? [languageSection] : []),
      {
        section: ExternalUrlSectionType.Details,
        visible: true,
        iconUrl: '/i/case-sections/default.svg',
        url: getSectionRoute(ExternalUrlSectionType.Details),
        label: (translation: ExternalUrlAdminTranslation) => translation.details,
        ref: detailsSectionRef,
        isValid: isValid([validationErrors.disabled, validationErrors.urlTemplate], ValidationLevel.error)
      },
      {
        section: ExternalUrlSectionType.Visibility,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(ExternalUrlSectionType.Visibility),
        label: (translation: ExternalUrlAdminTranslation) => "Visibility",
        ref: visibilitySectionRef,
        isValid: isValid([validationErrors.exclude], ValidationLevel.error)
      },
      ...(!renderLanguageOnTop ? [languageSection] : [])
    ];
  }, [id, actionType, detailsSectionRef, visibilitySectionRef, translationsSectionRef, validationErrors]);
};
